import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { formatIST, fmtDate } from '../utils/time';

// ─── Source icon map ───────────────────────────────────────────
const SOURCE_ICONS = {
  patrols:     '🚶',
  guards:      '👮',
  incidents:   '⚠️',
  checkpoints: '📍',
  shifts:      '📅',
};

// ─── Operator labels ───────────────────────────────────────────
const OPERATORS = [
  { v: '=',    l: 'equals' },
  { v: '!=',   l: 'not equals' },
  { v: 'LIKE', l: 'contains' },
  { v: '>=',   l: '>= (from)' },
  { v: '<=',   l: '<= (to)' },
  { v: '>',    l: 'greater than' },
  { v: '<',    l: 'less than' },
];

// ─── CSV export helper ─────────────────────────────────────────
function exportCSV(headers, rows) {
  const head = headers.map(h => `"${h.label}"`).join(',');
  const body = rows.map(row =>
    headers.map(h => {
      const v = row[h.key] ?? '';
      return `"${String(v).replace(/"/g, '""')}"`;
    }).join(',')
  ).join('\n');
  const blob = new Blob([head + '\n' + body], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url;
  a.download = `report_${Date.now()}.csv`; a.click();
  URL.revokeObjectURL(url);
}

// ─── JSON export helper ────────────────────────────────────────
function exportJSON(rows) {
  const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url;
  a.download = `report_${Date.now()}.json`; a.click();
  URL.revokeObjectURL(url);
}

// ─── Print helper ──────────────────────────────────────────────
function printReport(headers, rows, title) {
  const head = headers.map(h => `<th>${h.label}</th>`).join('');
  const body = rows.map(row =>
    `<tr>${headers.map(h => `<td>${row[h.key] ?? ''}</td>`).join('')}</tr>`
  ).join('');
  const w = window.open('', '_blank');
  w.document.write(`<html><head><title>${title}</title>
    <style>body{font-family:sans-serif;font-size:12px}table{border-collapse:collapse;width:100%}
    th,td{border:1px solid #ddd;padding:6px 10px;text-align:left}th{background:#4361ee;color:white}
    h2{color:#1a1a2e}@media print{button{display:none}}</style></head>
    <body><h2>${title}</h2><p>Generated: ${new Date().toLocaleString()}</p>
    <button onclick="window.print()">🖨 Print</button>
    <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`);
  w.document.close();
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export default function ReportBuilder() {
  // Schema from backend
  const [sources, setSources]       = useState({});
  const [savedReports, setSaved]    = useState([]);

  // Builder state
  const [source, setSource]         = useState('patrols');
  const [selCols, setSelCols]       = useState([]);
  const [filters, setFilters]       = useState([]);
  const [groupBy, setGroupBy]       = useState('');
  const [orderBy, setOrderBy]       = useState('');
  const [orderDir, setOrderDir]     = useState('ASC');
  const [rowLimit, setRowLimit]     = useState(500);
  const [reportName, setReportName] = useState('');

  // Results
  const [result, setResult]         = useState(null);
  const [running, setRunning]       = useState(false);

  // UI state
  const [tab, setTab]               = useState('builder'); // 'builder' | 'saved'
  const [saveModal, setSaveModal]   = useState(false);
  const [search, setSearch]         = useState('');

  // Load schema + saved reports
  useEffect(() => {
    axios.get('/api/reports/builder/sources').then(r => {
      setSources(r.data);
      // Set defaults for first source
      const first = r.data['patrols'];
      if (first) setSelCols(first.defaultCols);
    }).catch(() => toast.error('Failed to load report schema'));

    axios.get('/api/reports/saved').then(r => setSaved(r.data)).catch(() => {});
  }, []);

  // When source changes, reset to defaults
  useEffect(() => {
    if (sources[source]) {
      setSelCols(sources[source].defaultCols || []);
      setFilters([]);
      setGroupBy('');
      setOrderBy('');
      setResult(null);
    }
  }, [source]);

  const currentSource = sources[source];

  // ── Column toggle ────────────────────────────────────────────
  const toggleCol = (expr) => {
    setSelCols(prev =>
      prev.includes(expr) ? prev.filter(c => c !== expr) : [...prev, expr]
    );
  };

  const moveCol = (expr, dir) => {
    setSelCols(prev => {
      const idx = prev.indexOf(expr);
      if (idx < 0) return prev;
      const next = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return next;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  };

  // ── Filters ──────────────────────────────────────────────────
  const addFilter = () => {
    const fields = currentSource?.filterFields || [];
    if (!fields.length) return;
    setFilters(prev => [...prev, { field: fields[0].field, operator: '=', value: '' }]);
  };

  const updateFilter = (i, key, val) => {
    setFilters(prev => prev.map((f, idx) => idx === i ? { ...f, [key]: val } : f));
  };

  const removeFilter = (i) => setFilters(prev => prev.filter((_, idx) => idx !== i));

  // ── Run report ───────────────────────────────────────────────
  const runReport = async () => {
    if (selCols.length === 0) return toast.error('Select at least one column');
    setRunning(true);
    try {
      const res = await axios.post('/api/reports/builder/run', {
        source, columns: selCols, filters,
        groupBy: groupBy || undefined,
        orderBy: orderBy || undefined,
        orderDir, limit: rowLimit,
      });
      setResult(res.data);
      toast.success(`${res.data.total} row${res.data.total !== 1 ? 's' : ''} returned`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to run report');
    } finally {
      setRunning(false);
    }
  };

  // ── Save report ──────────────────────────────────────────────
  const saveReport = async () => {
    if (!reportName.trim()) return toast.error('Enter a report name');
    try {
      const config = { source, columns: selCols, filters, groupBy, orderBy, orderDir, limit: rowLimit };
      const saved = await axios.post('/api/reports/saved', { name: reportName, config });
      setSaved(prev => [saved.data, ...prev]);
      setSaveModal(false);
      setReportName('');
      toast.success('Report saved!');
    } catch (err) {
      toast.error('Failed to save report');
    }
  };

  // ── Load saved report ────────────────────────────────────────
  const loadReport = (report) => {
    const c = report.config;
    setSource(c.source || 'patrols');
    setTimeout(() => {
      setSelCols(c.columns || []);
      setFilters(c.filters || []);
      setGroupBy(c.groupBy || '');
      setOrderBy(c.orderBy || '');
      setOrderDir(c.orderDir || 'ASC');
      setRowLimit(c.limit || 500);
      setResult(null);
      setTab('builder');
    }, 100);
    toast.success(`Loaded: ${report.name}`);
  };

  const deleteReport = async (id) => {
    if (!window.confirm('Delete this saved report?')) return;
    await axios.delete(`/api/reports/saved/${id}`);
    setSaved(prev => prev.filter(r => r.id !== id));
    toast.success('Deleted');
  };

  // ── Cell formatter ───────────────────────────────────────────
  const fmtCell = (key, val) => {
    if (val === null || val === undefined) return <span style={{ color: '#ccc' }}>—</span>;
    if (key.includes('compliance') || key.includes('percent')) {
      const n = parseFloat(val);
      return (
        <span style={{ color: n >= 80 ? '#22c55e' : n >= 60 ? '#f59e0b' : '#ef233c', fontWeight: 600 }}>
          {n.toFixed(1)}%
        </span>
      );
    }
    if (key === 'status') {
      const colors = { active: '#f59e0b', completed: '#22c55e', abandoned: '#ef233c', scheduled: '#4361ee', missed: '#ef233c' };
      return <span className={`badge`} style={{ background: colors[val] || '#9ca3af', color: 'white' }}>{val}</span>;
    }
    if (key === 'severity') {
      const colors = { low: '#22c55e', medium: '#f59e0b', high: '#ef233c', critical: '#7c3aed' };
      return <span className="badge" style={{ background: colors[val] || '#9ca3af', color: 'white' }}>{val}</span>;
    }
    if (key.includes('_at') || key.includes('date')) {
      try { return formatIST(val, 'dd MMM yyyy HH:mm'); } catch { return val; }
    }
    if (key === 'is_active') return val == 1 ? '✅ Active' : '❌ Inactive';
    return String(val);
  };

  // ── Filtered rows for search ─────────────────────────────────
  const displayRows = search && result
    ? result.rows.filter(row =>
        Object.values(row).some(v => String(v ?? '').toLowerCase().includes(search.toLowerCase()))
      )
    : result?.rows || [];

  // ────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Page Header ───────────────────────────────────────── */}
      <div className="page-header" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="page-title">📊 Report Builder</div>
          <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 2 }}>
            Build custom reports from any data source
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => setTab(tab === 'builder' ? 'saved' : 'builder')}
            className="btn"
          >
            {tab === 'builder' ? `📁 Saved Reports (${savedReports.length})` : '🔧 Builder'}
          </button>
          {result && (
            <>
              <button onClick={() => exportCSV(result.headers, result.rows)} className="btn">
                ⬇️ CSV
              </button>
              <button onClick={() => exportJSON(result.rows)} className="btn">
                ⬇️ JSON
              </button>
              <button onClick={() => printReport(result.headers, result.rows, reportName || 'Custom Report')} className="btn">
                🖨️ Print
              </button>
            </>
          )}
          <button onClick={() => setSaveModal(true)} className="btn btn-primary" disabled={selCols.length === 0}>
            💾 Save Report
          </button>
        </div>
      </div>

      {/* ── Saved Reports tab ────────────────────────────────── */}
      {tab === 'saved' && (
        <div className="card">
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 16 }}>📁 Saved Report Templates</div>
          {savedReports.length === 0 && (
            <div style={{ color: '#9ca3af', padding: '40px 0', textAlign: 'center' }}>
              No saved reports yet. Build a report and click 💾 Save Report.
            </div>
          )}
          <div style={{ display: 'grid', gap: 12 }}>
            {savedReports.map(r => (
              <div key={r.id} style={{
                padding: '14px 18px', border: '1px solid #e5e7eb', borderRadius: 10,
                display: 'flex', alignItems: 'center', gap: 16, background: '#fafafa',
              }}>
                <div style={{ fontSize: 28 }}>{SOURCE_ICONS[r.config?.source] || '📊'}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{r.name}</div>
                  {r.description && <div style={{ fontSize: 12, color: '#9ca3af' }}>{r.description}</div>}
                  <div style={{ fontSize: 11, color: '#c4c9d4', marginTop: 3 }}>
                    Source: <strong>{r.config?.source}</strong> ·
                    {r.config?.columns?.length} columns ·
                    Created by {r.created_by_name} ·
                    {r.updated_at ? fmtDate(r.updated_at) : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => loadReport(r)} className="btn btn-primary" style={{ fontSize: 12 }}>
                    Load & Edit
                  </button>
                  <button onClick={() => deleteReport(r.id)} className="btn btn-danger" style={{ fontSize: 12 }}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Builder tab ──────────────────────────────────────── */}
      {tab === 'builder' && (
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20, alignItems: 'start' }}>

          {/* ── LEFT PANEL ──────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Data Source */}
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', background: '#f8f9fa', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: 13 }}>
                📦 Data Source
              </div>
              <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {Object.entries(sources).map(([key, def]) => (
                  <button
                    key={key}
                    onClick={() => setSource(key)}
                    style={{
                      padding: '10px 14px', borderRadius: 8, border: 'none',
                      textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                      background: source === key ? '#eef2ff' : 'transparent',
                      color: source === key ? '#4361ee' : '#374151',
                      fontWeight: source === key ? 600 : 400, fontSize: 13,
                      borderLeft: source === key ? '3px solid #4361ee' : '3px solid transparent',
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{SOURCE_ICONS[key]}</span>
                    {def.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Columns */}
            {currentSource && (
              <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', background: '#f8f9fa', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
                  <span>🗂 Columns</span>
                  <span style={{ fontWeight: 400, fontSize: 11, color: '#9ca3af' }}>{selCols.length} selected</span>
                </div>
                <div style={{ padding: 12, maxHeight: 320, overflowY: 'auto' }}>
                  {currentSource.columns.map(col => (
                    <div key={col.expr} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '5px 6px', borderRadius: 6,
                      background: selCols.includes(col.expr) ? '#f0f4ff' : 'transparent',
                      marginBottom: 2,
                    }}>
                      <input
                        type="checkbox"
                        checked={selCols.includes(col.expr)}
                        onChange={() => toggleCol(col.expr)}
                        style={{ cursor: 'pointer', accentColor: '#4361ee' }}
                      />
                      <span style={{ fontSize: 12, flex: 1, cursor: 'pointer', fontWeight: selCols.includes(col.expr) ? 500 : 400 }}
                        onClick={() => toggleCol(col.expr)}>
                        {col.label}
                      </span>
                      <span style={{ fontSize: 10, color: '#c4c9d4' }}>{col.type}</span>
                    </div>
                  ))}
                </div>
                {/* Column order */}
                {selCols.length > 0 && (
                  <div style={{ padding: '8px 12px', borderTop: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6, fontWeight: 600 }}>COLUMN ORDER</div>
                    {selCols.map((expr, i) => {
                      const meta = currentSource.columns.find(c => c.expr === expr);
                      return (
                        <div key={expr} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <span style={{ fontSize: 10, color: '#c4c9d4', width: 16 }}>{i + 1}.</span>
                          <span style={{ fontSize: 11, flex: 1 }}>{meta?.label || expr}</span>
                          <button onClick={() => moveCol(expr, -1)} disabled={i === 0}
                            style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? '#ddd' : '#4361ee', fontSize: 12 }}>↑</button>
                          <button onClick={() => moveCol(expr, 1)} disabled={i === selCols.length - 1}
                            style={{ background: 'none', border: 'none', cursor: i === selCols.length - 1 ? 'default' : 'pointer', color: i === selCols.length - 1 ? '#ddd' : '#4361ee', fontSize: 12 }}>↓</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Filters */}
            {currentSource && (
              <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', background: '#f8f9fa', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>🔍 Filters</span>
                  <button onClick={addFilter} style={{ background: '#4361ee', color: 'white', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}>
                    + Add
                  </button>
                </div>
                <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {filters.length === 0 && (
                    <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: '8px 0' }}>
                      No filters — showing all data
                    </div>
                  )}
                  {filters.map((f, i) => {
                    const ff = currentSource.filterFields || [];
                    const fieldMeta = ff.find(x => x.field === f.field);
                    return (
                      <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#666' }}>Filter {i + 1}</span>
                          <button onClick={() => removeFilter(i)} style={{ background: 'none', border: 'none', color: '#ef233c', cursor: 'pointer', fontSize: 14 }}>✕</button>
                        </div>
                        {/* Field */}
                        <select value={f.field} onChange={e => updateFilter(i, 'field', e.target.value)}
                          style={{ width: '100%', marginBottom: 6, padding: '5px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12 }}>
                          {ff.map(x => <option key={x.field} value={x.field}>{x.label}</option>)}
                        </select>
                        {/* Operator */}
                        <select value={f.operator} onChange={e => updateFilter(i, 'operator', e.target.value)}
                          style={{ width: '100%', marginBottom: 6, padding: '5px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12 }}>
                          {OPERATORS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                        {/* Value */}
                        {fieldMeta?.type === 'select' ? (
                          <select value={f.value} onChange={e => updateFilter(i, 'value', e.target.value)}
                            style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12 }}>
                            <option value="">— Any —</option>
                            {(fieldMeta.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input type={fieldMeta?.type === 'date' ? 'date' : 'text'}
                            value={f.value} placeholder="Value"
                            onChange={e => updateFilter(i, 'value', e.target.value)}
                            style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12, boxSizing: 'border-box' }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Group By + Order By */}
            {currentSource && (
              <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', background: '#f8f9fa', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: 13 }}>
                  🗃 Group & Sort
                </div>
                <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, display: 'block', marginBottom: 4 }}>GROUP BY</label>
                    <select value={groupBy} onChange={e => setGroupBy(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12 }}>
                      <option value="">— No grouping —</option>
                      {(currentSource.groupFields || []).map(g => (
                        <option key={g.field} value={g.field}>{g.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, display: 'block', marginBottom: 4 }}>ORDER BY</label>
                    <select value={orderBy} onChange={e => setOrderBy(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12 }}>
                      <option value="">— No sorting —</option>
                      {currentSource.columns.map(c => (
                        <option key={c.expr} value={c.expr}>{c.label}</option>
                      ))}
                    </select>
                    <select value={orderDir} onChange={e => setOrderDir(e.target.value)}
                      style={{ width: '100%', marginTop: 6, padding: '7px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12 }}>
                      <option value="ASC">Ascending ↑</option>
                      <option value="DESC">Descending ↓</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, display: 'block', marginBottom: 4 }}>MAX ROWS</label>
                    <select value={rowLimit} onChange={e => setRowLimit(Number(e.target.value))}
                      style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12 }}>
                      {[50, 100, 250, 500, 1000, 2000, 5000].map(n => (
                        <option key={n} value={n}>{n} rows</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Run Button */}
            <button
              onClick={runReport}
              disabled={running || selCols.length === 0}
              style={{
                padding: '14px', borderRadius: 10, border: 'none',
                background: running ? '#9ca3af' : '#4361ee',
                color: 'white', fontWeight: 700, fontSize: 15,
                cursor: running ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 12px rgba(67,97,238,0.3)',
              }}
            >
              {running ? '⏳ Running...' : '▶ Run Report'}
            </button>
          </div>

          {/* ── RIGHT PANEL — Results ───────────────────────── */}
          <div>
            {!result && !running && (
              <div style={{
                background: 'white', borderRadius: 12, border: '2px dashed #e5e7eb',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                minHeight: 400, color: '#9ca3af', gap: 12,
              }}>
                <div style={{ fontSize: 48 }}>📊</div>
                <div style={{ fontWeight: 600, fontSize: 16 }}>Configure and Run Your Report</div>
                <div style={{ fontSize: 13 }}>Select a data source, pick columns, add filters then click Run</div>
              </div>
            )}

            {running && (
              <div style={{
                background: 'white', borderRadius: 12, border: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                minHeight: 400, color: '#9ca3af',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
                  <div style={{ fontWeight: 600 }}>Running report...</div>
                </div>
              </div>
            )}

            {result && !running && (
              <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                {/* Result header */}
                <div style={{
                  padding: '14px 20px', background: '#f8f9fa', borderBottom: '1px solid #e5e7eb',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>{SOURCE_ICONS[source]} Results</span>
                    <span className="badge badge-info">{result.total} rows</span>
                    {result.total === rowLimit && (
                      <span className="badge badge-warning">⚠️ Limit reached — increase max rows</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      placeholder="🔍 Search results..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12, width: 200 }}
                    />
                    <button onClick={() => exportCSV(result.headers, result.rows)} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12, cursor: 'pointer', background: 'white' }}>⬇️ CSV</button>
                    <button onClick={() => exportJSON(result.rows)} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12, cursor: 'pointer', background: 'white' }}>⬇️ JSON</button>
                    <button onClick={() => printReport(result.headers, result.rows, 'Custom Report')} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12, cursor: 'pointer', background: 'white' }}>🖨️</button>
                  </div>
                </div>

                {/* Table */}
                <div style={{ overflowX: 'auto', maxHeight: 560, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                        {result.headers.map(h => (
                          <th key={h.key} style={{
                            padding: '10px 14px', textAlign: 'left', fontWeight: 600,
                            background: '#1a1a2e', color: 'white', whiteSpace: 'nowrap',
                            borderRight: '1px solid rgba(255,255,255,0.1)',
                          }}>
                            {h.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayRows.length === 0 && (
                        <tr>
                          <td colSpan={result.headers.length} style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                            {search ? `No rows match "${search}"` : 'No data returned'}
                          </td>
                        </tr>
                      )}
                      {displayRows.map((row, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                          {result.headers.map(h => (
                            <td key={h.key} style={{
                              padding: '9px 14px', borderBottom: '1px solid #f3f4f6',
                              maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {fmtCell(h.key, row[h.key])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Footer stats */}
                <div style={{ padding: '10px 20px', background: '#f8f9fa', borderTop: '1px solid #e5e7eb', fontSize: 12, color: '#9ca3af', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Showing {displayRows.length} of {result.total} rows{search ? ` (filtered by "${search}")` : ''}</span>
                  <span>Source: <strong>{sources[source]?.label}</strong> · {result.headers.length} columns</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Save Modal ──────────────────────────────────────── */}
      {saveModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 20 }}>💾 Save Report Template</div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: 6, fontSize: 13 }}>Report Name *</label>
              <input
                type="text"
                value={reportName}
                onChange={e => setReportName(e.target.value)}
                placeholder="e.g. Weekly Patrol Compliance"
                autoFocus
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 20, padding: '10px 14px', background: '#f8f9fa', borderRadius: 8 }}>
              <strong>Source:</strong> {sources[source]?.label} ·
              <strong> Columns:</strong> {selCols.length} ·
              <strong> Filters:</strong> {filters.length}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setSaveModal(false); setReportName(''); }} className="btn">Cancel</button>
              <button onClick={saveReport} className="btn btn-primary">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
